import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./FinancingSection.module.scss";
import { CaretDownIcon } from "@/frontend/shared/assets/icons";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { RefObject, useState } from "react";
import clsx from "clsx";
import { bigNumberConverter } from "@/frontend/shared/lib/bigNumberConverter/bigNumberConverter";
import { convertCompanyStatus } from "@/frontend/shared/lib/convertCompanyStatus/convertCompanyStatus";
import { formatDateMonthYear } from "@/frontend/shared/lib/formatDateMonthYear/formatDateMonthYear";
import { SectionContentWrapper } from "@/frontend/widgets/SectionContentWrapper/SectionContentWrapper";
import { Logo } from "@/frontend/shared/ui/Logo";
import { InnoveraBlockNoData } from "../../../ui/InnoveraBlockNoData/InnoveraBlockNoData";
import { ESectionOptions } from "@/frontend/widgets/Sidebar";
import { SectionLoader } from "../Section/SectionLoader/SectionLoader";
import { SectionTitle } from "../Section/SectionTitle/SectionTitle";
import { TCompanyData, TCompanyProfile } from "../../../model/types/types";

export type TFinancingSectionProps = {
  innerRef: RefObject<HTMLDivElement>;
  className: string;
  innoveraData?: TCompanyData | TCompanyProfile;
  isLoadingInnoveraData: boolean;
  onReportError?: () => void;
};
export function FinancingSection({
  innerRef,
  className,
  innoveraData,
  onReportError,
  isLoadingInnoveraData: isLoading
}: TFinancingSectionProps) {
  const [isInvestorsShown, setIsInvestorsShown] = useState(false);
  const investorsShownData = isInvestorsShown
    ? innoveraData?.harmonicInvestors
    : innoveraData?.harmonicInvestors?.slice(0, 4);
  const content = !!innoveraData ? (
    <div className={styles.innoveraBlock}>
      <div className={styles.containersInnoveraBlockContent}>
        <div className={styles.contentSubContainer}>
          <Typography variant="label-4" color="greyscale600">
            Series
          </Typography>
          <Typography variant="label-3" color="greyscale900">
            {(!!innoveraData?.financialStage && convertCompanyStatus(innoveraData?.financialStage)) || "-"}
          </Typography>
        </div>
        <div className={styles.contentSubContainer}>
          <Typography variant="label-4" color="greyscale600">
            Last Funding Amount
          </Typography>
          <Typography variant="label-3" color="greyscale900">
            {(innoveraData?.lastFundingAmount && `$${bigNumberConverter(innoveraData?.lastFundingAmount)}`) || "-"}
          </Typography>
        </div>
        <div className={styles.contentSubContainer}>
          <Typography variant="label-4" color="greyscale600">
            Date
          </Typography>
          <Typography variant="label-3" color="greyscale900">
            {formatDateMonthYear(innoveraData?.lastFundingDate || null)}
          </Typography>
        </div>
        <div className={styles.contentSubContainer}>
          <Typography variant="label-4" color="greyscale600">
            Funding Rounds
          </Typography>
          <Typography variant="label-3" color="greyscale900">
            {innoveraData?.fundingRoundsCount || "-"}
          </Typography>
        </div>
        <div className={styles.contentSubContainer}>
          <Typography variant="label-4" color="greyscale600">
            Total funding
          </Typography>
          <Typography variant="label-3" color="greyscale900">
            {(innoveraData?.totalFunding && `$${bigNumberConverter(innoveraData?.totalFunding)}`) || "-"}
          </Typography>
        </div>
      </div>
      {innoveraData.harmonicInvestors?.length > 0 && (
        <div className={styles.innoveraBottomBlockContent}>
          <Typography variant="heading-7" color="greyscale900" className={styles.investorsTitle}>
            Investors
          </Typography>

          <div className={styles.investorsContainer}>
            {investorsShownData?.map((investor: { name: string; type: string; imageUrl: string | null }) => (
              <div className={styles.investorContainer} key={investor.name}>
                <Logo size="normal" logo={investor.imageUrl} />
                <div className={styles.investorTextContainer}>
                  <Typography variant="label-4" color="greyscale900" className={styles.investorName}>
                    {investor.name}
                  </Typography>
                  <Typography variant="body-3" color="greyscale600" className={styles.investorType}>
                    {investor.type}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
          {innoveraData.harmonicInvestors?.length > 4 && (
            <NormalButton
              variant="primary"
              size="small"
              color="greyscale100"
              className={styles.seeMoreBtn}
              onClick={() => setIsInvestorsShown((prev) => !prev)}
            >
              <CaretDownIcon className={clsx(styles.seeMoreBtnIcon, isInvestorsShown && styles.seeMoreBtnIconActive)} />
              {isInvestorsShown ? "Hide" : "Show more"}
            </NormalButton>
          )}
        </div>
      )}
    </div>
  ) : isLoading ? (
    <SectionLoader />
  ) : (
    <InnoveraBlockNoData />
  );

  return (
    <div className={className}>
      <div className={styles.container}>
        <div className={styles.refContainer} data-section={ESectionOptions.FINANCING} ref={innerRef}>
          <div className={styles.contentContainer}>
            <div className={styles.titleContainer}>
              <SectionTitle>Financing</SectionTitle>
            </div>
            <SectionContentWrapper isByInnovera onReportError={onReportError} companyName="Crunchbase">
              {content}
            </SectionContentWrapper>
          </div>
        </div>
      </div>
    </div>
  );
}
